import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, LogOut, Settings, Users, Building2, UserSearch, Home, Briefcase, BarChart3, CheckSquare } from 'lucide-react';
import { getAvatarUrl, DEFAULT_AVATAR_ID } from '../config/avatars';

const Navigation = () => {
  const { userProfile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  const avatarUrl = getAvatarUrl(userProfile?.avatarUrl || DEFAULT_AVATAR_ID);

  const getRoleLabel = () => {
    if (!userProfile) return '';
    const roleCode = (() => {
      const roleMap = { 'jobasti_admin': 'JADM', 'client_admin': 'RADM', 'client_recruiter': 'RECR', 'prospect': 'PROS', 'sales_executive': 'SEXE' };
      return roleMap[userProfile.role] || userProfile.role;
    })();
    switch (roleCode) {
      case 'JADM': return 'Admin';
      case 'RADM': return 'Administrator';
      case 'RECR': return 'Recruiter';
      case 'PROS': return 'Applicant';
      case 'SEXE': return 'Sales Executive';
      default: return '';
    }
  };

  const displayName = userProfile?.firstName && userProfile?.lastName
    ? `${userProfile.firstName} ${userProfile.lastName}`
    : userProfile?.username || userProfile?.email || '';

  // Navigation items based on user role
  const getNavigationItems = () => {
    if (!userProfile) return [];

    // Map database roles to frontend role codes
    const roleMap = {
      'jobasti_admin': 'JADM',
      'jobasti_recruiter': 'JREC',
      'client_admin': 'CADM',
      'client_recruiter': 'CREC',
      'prospect': 'PROS',
      'sales_executive': 'SEXE'
    };

    const userRole = roleMap[userProfile.role] || userProfile.role;

    const baseItems = [
      { path: '/dashboard', label: t('appNav.dashboard'), icon: Home, noTranslate: true }
    ];

    switch (userRole) {
      case 'JADM': // Jobasti Admin (Super Admin)
        return [
          { path: '/admin/dashboard', label: t('appNav.dashboard'), icon: Home, noTranslate: true },
          { path: '/admin/companies', label: 'Companies', icon: Building2 },
          { path: '/admin/users', label: 'Users', icon: Users },
          { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },

          { path: '/sourcing', label: 'Sourcing', icon: UserSearch },
        ];
      case 'SEXE': // Jobasti Sales Executive (internal CRM)
        return [
          { path: '/sales-dashboard', label: t('appNav.dashboard'), icon: Home, noTranslate: true },
          { path: '/accounts', label: 'Accounts', icon: Building2 },
          { path: '/crm-contacts', label: 'Contacts', icon: Users },
          { path: '/crm-activities', label: 'Activities', icon: BarChart3 },
          { path: '/sales-tasks', label: 'Tasks', icon: CheckSquare },
        ];
      case 'CADM': // Client Admin - has Company management
        return [
          ...baseItems,
          { path: '/candidates', label: 'Candidates', icon: Users },
          { path: '/positions', label: 'Jobs', icon: Briefcase },
          { path: '/my-company', label: 'Company', icon: Building2 },
        ];
      case 'CREC': // Client Recruiter
        return [
          ...baseItems,
          { path: '/candidates', label: 'Candidates', icon: Users },
          { path: '/positions', label: 'Jobs', icon: Briefcase },
          { path: '/my-company', label: 'Company', icon: Building2 },
        ];
      case 'PROS': // Prospect
        return [
          { path: '/prospect-dashboard', label: t('appNav.dashboard'), icon: Home, noTranslate: true },
          { path: '/jobs', label: 'Job Search', icon: Briefcase },
          { path: '/my-jobs', label: 'My Jobs', icon: User },
        ];
      default:
        return baseItems;
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo - Left */}
          <div className="flex-shrink-0">
            <Link to="/dashboard">
              <h1 className="text-2xl font-bold text-gray-900 notranslate" translate="no">Jobasti<span className="text-blue-600">IQ</span></h1>
            </Link>
          </div>
          
          {/* Navigation Items - Center */}
          <div className="flex-1 flex justify-center">
            <div className="hidden md:flex md:space-x-8">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isActive
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.noTranslate ? (
                      <span className="notranslate" translate="no">{item.label}</span>
                    ) : item.label}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* User Menu - Right */}
          <div className="flex-shrink-0 flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2.5 h-auto py-1.5 px-2" data-testid="user-menu-trigger">
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-gray-200"
                  />
                  <div className="hidden md:flex flex-col items-start">
                    <span className="text-sm font-semibold text-gray-800 leading-tight">{displayName}</span>
                    <span className="text-[11px] text-blue-600 font-medium leading-tight">{getRoleLabel()}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{displayName}</p>
                  <p className="text-xs text-gray-500">{userProfile?.email}</p>
                  <p className="text-xs text-blue-600">{getRoleLabel()}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(userProfile?.role === 'prospect' || userProfile?.role === 'PROS' ? '/my-profile' : '/user-profile')}>
                  <User className="w-4 h-4 mr-2" />
                  User Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;